// Attribution: https://jasonwatmore.com/post/2020/04/11/react-hooks-bootstrap-alert-notifications

// Dependencies
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router";

// Material
import Alert, { AlertColor } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

// Services
import { alertService, AlertType } from "@/services/alert";

interface AlertCommonProps {
  id?: string;
  fade?: boolean;
}

interface AlertDefinition {
  type: (typeof AlertType)[keyof typeof AlertType];
  message: string;
  autoClose?: boolean;
  keepAfterRouteChange?: boolean;
  fade?: boolean;
  autoFocus?: boolean;
  onClose?: () => void;
}

const AlertCommon: React.FC<AlertCommonProps> = ({
  id = "default-id",
  fade = true,
}) => {
  const [alerts, setAlerts] = useState<AlertDefinition[]>([]);
  const location = useLocation();
  const alertRef = useRef<HTMLDivElement>(null);

  const removeAlert = useCallback(
    (alert: AlertDefinition) => {
      if (fade) {
        // fade out alert
        const alertWithFade = { ...alert, fade: true };
        setAlerts((alerts) =>
          alerts.map((x) => (x === alert ? alertWithFade : x)),
        );

        // remove alert after faded out
        setTimeout(() => {
          setAlerts((alerts) => alerts.filter((x) => x !== alertWithFade));
          if (alert.onClose) {
            alert.onClose();
          }
        }, 250);
      } else {
        // remove alert
        setAlerts((alerts) => alerts.filter((x) => x !== alert));
        if (alert.onClose) {
          alert.onClose();
        }
      }
    },
    [fade],
  );

  useEffect(() => {
    // subscribe to new alert notifications
    const subscription = alertService
      .onAlert(id)
      .subscribe((alert: AlertDefinition | null) => {
        // clear alerts when an empty alert is received
        if (!alert?.message) {
          setAlerts((alerts) => {
            // filter out alerts without 'keepAfterRouteChange' flag
            const filteredAlerts = alerts.filter((x) => x.keepAfterRouteChange);

            // remove 'keepAfterRouteChange' flag on the rest
            filteredAlerts.forEach((x) => delete x.keepAfterRouteChange);
            return filteredAlerts;
          });
        } else {
          // add alert to array
          setAlerts((alerts) => [...alerts, alert]);

          if (alert.autoFocus) {
            // Use a small timeout to allow the alert to render into DOM
            setTimeout(() => {
              const el = document.querySelector(
                '[data-alert-auto-focus="true"]',
              ) as HTMLElement | null;
              if (el) {
                try {
                  el.focus({ preventScroll: true } as any);
                } catch {
                  el.focus();
                }
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                // Remove the marker so subsequent alerts can be focused correctly
                el.removeAttribute("data-alert-auto-focus");
              }
            }, 50);
          }

          // auto close alert if required (6seconds as per accessibility)
          if (alert.autoClose) {
            setTimeout(() => removeAlert(alert), 6000);
          }

          // set focus to error alerts for accessibility
          if (alert.type === AlertType.Error) {
            setTimeout(() => alertRef.current?.focus(), 100);
          }
        }
      });

    // clean up function that runs when the component unmounts
    return () => {
      // unsubscribe to avoid memory leaks
      subscription.unsubscribe();
      alertService.clear(id);
    };
  }, [location, id, removeAlert]);

  function cssClasses(alert: AlertDefinition): AlertColor {
    if (!alert) return "info";

    const alertTypeClass: Record<
      (typeof AlertType)[keyof typeof AlertType],
      AlertColor
    > = {
      [AlertType.Success]: "success",
      [AlertType.Error]: "error",
      [AlertType.Info]: "info",
      [AlertType.Warning]: "warning",
    };

    return alertTypeClass[alert.type];
  }

  if (!alerts.length) return null;

  return (
    <div ref={alertRef} tabIndex={-1}>
      {alerts.map((alert, index) => (
        <Alert
          key={index}
          severity={cssClasses(alert)}
          onClose={() => {
            removeAlert(alert);
          }}
          variant="outlined"
          data-alert-auto-focus={alert.autoFocus ? "true" : "false"}
          sx={{ borderLeft: 15, marginBottom: 1 }}
        >
          <AlertTitle sx={{ fontWeight: "bold" }}>{alert.type}</AlertTitle>
          <span>{alert.message}</span>
        </Alert>
      ))}
    </div>
  );
};

export { AlertCommon };
